import Component from '@glimmer/component'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { service } from '@ember/service'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { ProgramPanel } from 'teacher-ui-client/components/copy-classes/program-panel'
import { SummaryPanel } from 'teacher-ui-client/components/copy-classes/summary-panel'
import { TeacherOptionTypes } from 'teacher-ui-client/components/base/select/teacher/options'
import { ClassesPanel } from 'teacher-ui-client/components/copy-classes/classes-panel'
import { t } from 'ember-intl'
import { PageHeader } from 'teacher-ui-client/components/page-header/index'
import { on } from '@ember/modifier'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { action } from '@ember/object'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import { didCancel, task, timeout } from 'ember-concurrency'
import { waitFor } from '@ember/test-waiters'
import type Store from '@ember-data/store'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type { CreateSchoolClassEvent } from 'teacher-ui-client/analytics/events'
import type AnalyticsService from 'teacher-ui-client/services/analytics'
import type SessionService from 'teacher-ui-client/services/session'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import { tracked } from '@glimmer/tracking'
import { SuccessModal } from 'teacher-ui-client/components/copy-classes/success-modal'
import { assert } from '@ember/debug'
import { ProgressModal } from 'teacher-ui-client/components/copy-classes/progress-modal'
import { schoolClassToChangeset, type FromClassRow } from 'teacher-ui-client/components/copy-classes/from-class-option'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type { Log } from '@blakeelearning/log'
import { UnknownErrorAlert } from 'teacher-ui-client/components/copy-classes/unknown-error-alert'
import { ClassStudentLimitAlert } from 'teacher-ui-client/components/copy-classes/class-student-limit-alert'
import config from 'teacher-ui-client/config/environment'
import { hasTrialTeacherError } from 'teacher-ui-client/errors/trial-teacher-error'
import { TrialTeacherAlert } from 'teacher-ui-client/components/copy-classes/trial-teacher-alert'

const MAX_STUDENTS_PER_CLASS = config.copyClasses.maxStudentsPerClass

enum CopyClassesError {
  ResourceConflict,
  TrialTeacher,
  Cancel,
  Unknown,
}

export class CopyClassesPage extends Component<unknown> {
  @service subscriptionType!: SubscriptionTypeService

  @service store!: Store

  @service analytics!: AnalyticsService

  @service session!: SessionService

  @service resourceConflict!: ResourceConflictService

  @service log!: Log

  @queryParam('subscription-type/copy-classes') fromSubscriptionType?: SubscriptionType

  @queryParam('subscription-type/copy-classes') selectedIds!: string[]

  @queryParam('subscription-type/copy-classes') hideEmptyClasses!: boolean

  @queryParam('subscription-type/copy-classes') teacherId?: TeacherOptionTypes | string

  @tracked showSuccessModal = false

  get defaultTeacherId(): TeacherOptionTypes {
    return this.isSubco ? TeacherOptionTypes.All : TeacherOptionTypes.Current
  }

  get school() {
    return this.session.currentSchool
  }

  get currentTeacher() {
    return this.session.currentTeacher
  }

  get toSubscriptionType() {
    return this.subscriptionType.current ?? SubscriptionType.Reading
  }

  get classCount() {
    return this.selectedIds?.length ?? 0
  }

  get studentCount() {
    return this.selectedOptions.reduce((studentCount: number, fromClassRow: FromClassRow) => {
      return studentCount + fromClassRow.studentCount
    }, 0)
  }

  get noneInvalid() {
    return !this.selectedOptions.some((fromClassRow) => fromClassRow.get('isInvalid'))
  }

  get submitButtonEnabled() {
    return this.classCount > 0 && this.noneInvalid && !this.isClassStudentLimitExceeded
  }

  @action
  updateFromSubscriptionType(subscriptionType: SubscriptionType) {
    this.fromSubscriptionType = subscriptionType
    this.selectedIds = []
  }

  @action
  updateTeacherId(teacherId: TeacherOptionTypes | string) {
    this.teacherId = teacherId
    this.selectedIds = []
  }

  @action
  resetProgramSection() {
    this.fromSubscriptionType = undefined
    this.resetClassesSection()
  }

  @action
  resetClassesSection() {
    this.selectedIds = []
    this.teacherId = this.defaultTeacherId
  }

  @action
  async submit() {
    assert('fromSubscriptionType must be defined when calling submit method', this.fromSubscriptionType)
    try {
      const result = await this.copySchoolClasses.perform(this.selectedOptions)
      if (result.ok) {
        this.showSuccessModal = true
        return
      }
      result.errors.forEach((error) => this.handleError(error))
    } catch (error: unknown) {
      if (didCancel(error)) {
        return
      }
    }
  }

  handleError(error: { kind: CopyClassesError; sourceError: unknown }) {
    switch (error.kind) {
      case CopyClassesError.ResourceConflict:
        this.resourceConflict.handleError('copy classes', error.sourceError)
        return
      case CopyClassesError.Unknown:
        this.log.error('unknown copy classes error', error)
        return
    }
  }

  isVisibleSchoolClass(schoolClass: SchoolClass, hideEmptyClasses: boolean) {
    const isNotHidden = hideEmptyClasses ? schoolClass.hasStudents : true
    return isNotHidden
  }

  @action
  onCloseSuccessModal() {
    this.showSuccessModal = false
    this.resetClassesSection()
  }

  @action
  onCancelCopySchoolClasses() {
    // do nothing when user clicks outside modal
  }

  get isSubco() {
    return this.currentTeacher.subcoFor(this.toSubscriptionType)
  }

  get schoolClasses() {
    return this.fetchSchoolClasses.lastSuccessful?.value ?? []
  }

  get selectedOptions(): FromClassRow[] {
    return this.schoolClasses.filter((changeset: FromClassRow) =>
      this.selectedIds.includes(String(changeset.get('id'))),
    )
  }

  fetchSchoolClasses = task(
    { restartable: true },
    waitFor(
      async ({
        subscriptionType,
        teacherId,
        hideEmptyClasses,
      }: {
        subscriptionType: SubscriptionType
        teacherId?: TeacherOptionTypes | string
        hideEmptyClasses: boolean
      }) => {
        await timeout(100)
        const teacherIdOrDefault = teacherId ?? this.defaultTeacherId
        const isAllTeachers = teacherIdOrDefault === TeacherOptionTypes.All.toString()
        const isCurrentTeacher = teacherIdOrDefault === TeacherOptionTypes.Current.toString()
        try {
          const filter: {
            product: SubscriptionType
            teachers?: {
              id: string
            }
          } = { product: subscriptionType }
          if (!isAllTeachers)
            filter.teachers = {
              id: isCurrentTeacher ? this.currentTeacher.id : teacherIdOrDefault,
            }
          const schoolClasses = await this.store.query<SchoolClass>('school-class', {
            filter,
            // @ts-expect-error - include types not correct
            include: 'students,teachers',
          })
          return schoolClasses
            .filter((schoolClass) => this.isVisibleSchoolClass(schoolClass, hideEmptyClasses))
            .map((schoolClass) => schoolClassToChangeset(this.toSubscriptionType, schoolClass))
        } catch {
          return []
        }
      },
    ),
  )

  get showTrialTeacherError() {
    if (this.copySchoolClasses.isRunning) return false
    const result = this.copySchoolClasses.lastComplete?.value
    return result?.ok === false && result.errors.some(({ kind }) => kind === CopyClassesError.TrialTeacher)
  }

  get showUnknownError() {
    if (this.copySchoolClasses.isRunning) return false
    const result = this.copySchoolClasses.lastComplete?.value
    return result?.ok === false && result.errors.some(({ kind }) => kind === CopyClassesError.Unknown)
  }

  get isClassStudentLimitExceeded() {
    return Boolean(this.selectedOptions.some((schoolClass) => schoolClass.studentCount > MAX_STUDENTS_PER_CLASS))
  }

  copySchoolClasses = task(
    { restartable: true },
    waitFor(async (fromClassRows: FromClassRow[]) => {
      try {
        const savePromises = fromClassRows.map(async (fromClassRow) => {
          const draftSchoolClass = this.draftNewSchoolClass(fromClassRow)
          return draftSchoolClass.save({ adapterOptions: { bulk: true } })
        })

        const minimumDuration = timeout(2000)
        const [, ...createdClasses] = await Promise.all([minimumDuration, ...savePromises])

        this.logCreatedClasses(createdClasses)

        return {
          ok: true as const,
          schoolClasses: createdClasses,
        }
      } catch (error: unknown) {
        this.unloadDraftClasses()
        return this.buildCopyClassesError(error)
      }
    }),
  )

  private draftNewSchoolClass(newSchoolClass: FromClassRow): SchoolClass {
    return this.store.createRecord<SchoolClass>('school-class', {
      name: newSchoolClass.name,
      students: newSchoolClass.students,
      teachers: this.isSubco ? newSchoolClass.teachers : [this.currentTeacher],
      school: this.school,
      subscriptionType: this.toSubscriptionType,
      gamesAccess: newSchoolClass.gamesAccess,
      playroomAccess: newSchoolClass.playroomAccess,
      mathseedsPrimeAccess: newSchoolClass.mathseedsPrimeAccess,
      backgroundMusicDisabled: newSchoolClass.backgroundMusicDisabled,
    })
  }

  private logCreatedClasses(createdClasses: SchoolClass[]) {
    createdClasses.forEach((createdClass) => {
      const createSchoolClassEventArgs: CreateSchoolClassEvent = [
        'Create class',
        {
          source: 'copied',
          subscriptionType: createdClass.subscriptionType,
        },
      ]
      this.analytics.capture(...createSchoolClassEventArgs)
    })
  }

  /**
   * the interrogation/type narrowing of caught errors happens here in the
   * service so that consumers have a simple error interface to work with.
   */
  private buildCopyClassesError(error: unknown) {
    if (didCancel(error)) {
      return {
        ok: false as const,
        errors: [
          {
            kind: CopyClassesError.Cancel,
            sourceError: error,
          },
        ],
      }
    }
    if (isConflictError(error)) {
      return {
        ok: false as const,
        errors: [
          {
            kind: CopyClassesError.ResourceConflict,
            sourceError: error,
          },
        ],
      }
    }
    // handle multiple validation errors or unknown error
    const result: {
      ok: false
      errors: Array<
        | { kind: CopyClassesError.TrialTeacher; sourceError: unknown }
        | { kind: CopyClassesError.Unknown; sourceError: unknown }
      >
    } = {
      ok: false,
      errors: [],
    }
    if (hasTrialTeacherError(error) && this.isSubco) {
      result.errors.push({
        kind: CopyClassesError.TrialTeacher as const,
        sourceError: error,
      })
    }
    if (result.errors.length === 0) {
      result.errors.push({
        kind: CopyClassesError.Unknown as const,
        sourceError: error,
      })
    }
    return result
  }

  private unloadDraftClasses() {
    this.store
      .peekAll<SchoolClass>('school-class')
      .filter((schoolClass) => schoolClass.isNew)
      .forEach((schoolClass) => schoolClass.unloadRecord())
  }

  historyBack = () => history.back(); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    <div class="w-full">
      <PageHeader class="mb-4">
        <:title>
          <h1>
            {{t "components.copyClasses.title"}}
          </h1>
        </:title>
        <:left>
          <button type="button" {{on "click" this.historyBack}}>
            <PhosphorIcon @icon="phosphor-caret-left-bold" class="h-3 w-3" />
            <span class="underline">{{t "back"}}</span>
          </button>
        </:left>
      </PageHeader>
      <p class="text-center">
        {{t "components.copyClasses.prompt" program=(t this.toSubscriptionType)}}<br />
        {{t "components.copyClasses.effect"}}
      </p>
      <TrialTeacherAlert class="mt-4" @show={{this.showTrialTeacherError}} />
      <ClassStudentLimitAlert class="mt-4" @show={{this.isClassStudentLimitExceeded}} />
      <UnknownErrorAlert class="mt-4" @show={{this.showUnknownError}} />
      <div class="mt-8 flex flex-nowrap items-start gap-8">
        <div class="flex flex-1 flex-col space-y-8">
          <ProgramPanel
            @fromSubscriptionType={{this.fromSubscriptionType}}
            @toSubscriptionType={{this.toSubscriptionType}}
            @updateSubscriptionType={{this.updateFromSubscriptionType}}
            @reset={{this.resetProgramSection}}
          />
          {{#if this.fromSubscriptionType}}
            <ClassesPanel
              @isSubco={{this.isSubco}}
              @toSubscriptionType={{this.toSubscriptionType}}
              @updateTeacherId={{this.updateTeacherId}}
              @reset={{this.resetClassesSection}}
              @fetchSchoolClasses={{this.fetchSchoolClasses}}
            />
          {{/if}}
        </div>
        <SummaryPanel
          class="sticky top-2 basis-80"
          @fromSubscriptionType={{this.fromSubscriptionType}}
          @toSubscriptionType={{this.toSubscriptionType}}
          @classCount={{this.classCount}}
          @studentCount={{this.studentCount}}
          @submitButtonEnabled={{this.submitButtonEnabled}}
          @onSubmit={{this.submit}}
        />
      </div>
    </div>
    {{#if this.fromSubscriptionType}}
      <SuccessModal
        @showModal={{this.showSuccessModal}}
        @fromSubscriptionType={{this.fromSubscriptionType}}
        @toSubscriptionType={{this.toSubscriptionType}}
        @classCount={{this.classCount}}
        @onClose={{this.onCloseSuccessModal}}
      />
      <ProgressModal
        @showModal={{this.copySchoolClasses.isRunning}}
        @fromSubscriptionType={{this.fromSubscriptionType}}
        @toSubscriptionType={{this.toSubscriptionType}}
        @classCount={{this.classCount}}
        @onCancel={{this.onCancelCopySchoolClasses}}
      />
    {{/if}}
  </template>
}

export default CopyClassesPage
